import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Users, Clock, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { nl } from "date-fns/locale";
import { format, addDays, subDays } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Table component showing reservations
const OverviewTable = ({ table, tableReservations }) => {
  const [isHovered, setIsHovered] = useState(false);
  const reservationCount = tableReservations.length;
  const hasMultiple = reservationCount > 1;
  
  const getStatusColor = () => {
    if (hasMultiple) return "#F59E0B"; // Orange for multiple reservations
    if (reservationCount === 1) return "#6366F1"; // Purple when occupied
    return "#374151"; // Gray when free
  };

  const isBarSeat = table.type === 'bar';
  const isDecor = table.type === 'decor';
  
  if (isDecor) {
    return (
      <div
        className="absolute rounded border-2 border-dashed border-gray-700 flex items-center justify-center"
        style={{
          left: `${table.x}px`,
          top: `${table.y}px`,
          transform: 'translate(-50%, -50%)',
          width: table.width || '120px',
          height: table.height || '60px',
          backgroundColor: '#2A2520'
        }}
      >
        <span className="text-xs text-gray-500">{table.name}</span>
      </div>
    );
  }

  const shapeClass = table.shape === 'circular' ? 'rounded-full' : 
                     table.shape === 'square' ? 'rounded-md' : 'rounded-lg';
  
  const width = isBarSeat ? '40px' : 
                table.shape === 'circular' ? '80px' : 
                table.shape === 'square' ? '60px' : '100px';
  const height = isBarSeat ? '40px' : 
                 table.shape === 'circular' ? '80px' : 
                 table.shape === 'square' ? '60px' : '65px';

  const tableLabel = isBarSeat ? `B${table.number}` : `T${table.number}`;
  
  // Get first reservation info for compact display
  const firstRes = tableReservations[0];

  return (
    <div
      className="absolute"
      style={{
        left: `${table.x}px`,
        top: `${table.y}px`,
        transform: 'translate(-50%, -50%)',
        zIndex: isHovered ? 100 : (reservationCount > 0 ? 20 : 10)
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Table */}
      <div
        className={`${shapeClass} border-2 flex flex-col items-center justify-center transition-all relative cursor-pointer`}
        style={{
          width,
          height,
          backgroundColor: getStatusColor(),
          borderColor: isHovered ? '#D4AF37' : (reservationCount > 0 ? '#fff' : '#555'),
          boxShadow: isHovered ? '0 0 15px rgba(212, 175, 55, 0.5)' : 'none'
        }}
      >
        <span className="text-xs font-bold text-white">{tableLabel}</span>
        {!isBarSeat && reservationCount === 0 && (
          <span className="text-[10px] text-white/70">{table.capacity}p</span>
        )}
        {/* Show time on table when occupied (non-bar) */}
        {!isBarSeat && reservationCount > 0 && firstRes && (
          <span className="text-[10px] text-white font-medium">{firstRes.time}</span>
        )}
        
        {/* Badge for multiple reservations */}
        {hasMultiple && (
          <div className="absolute -top-2 -right-2 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center text-xs font-bold text-white border border-white">
            {reservationCount}
          </div>
        )}
      </div>

      {/* Reservation info tooltip - only visible on hover */}
      {reservationCount > 0 && isHovered && (
        <div 
          className="absolute left-1/2 -translate-x-1/2 mt-2 bg-[#1A1A1A] border border-brand-gold/50 rounded-lg p-3 min-w-[180px] shadow-2xl"
          style={{ top: '100%' }}
        >
          <div className="text-xs text-gray-400 mb-2 pb-2 border-b border-white/10">
            {tableLabel} • {table.capacity} personen capaciteit
          </div>
          {tableReservations.map((res, idx) => (
            <div 
              key={res.id} 
              className={`${idx > 0 ? 'mt-3 pt-3 border-t border-white/10' : ''}`}
            >
              <div className="flex items-center gap-2 text-brand-gold font-semibold">
                <Clock className="w-4 h-4" />
                {res.time}
              </div>
              <p className="text-white font-medium mt-1">
                {res.guest_name || 'Onbekend'}
              </p>
              <div className="flex items-center gap-1 text-gray-400 text-sm mt-1">
                <Users className="w-3 h-3" />
                {res.party_size} personen
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FloorPlanOverview = () => {
  const [tables, setTables] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const [tablesRes, reservationsRes] = await Promise.all([
        axios.get(`${API}/tables`),
        axios.get(`${API}/reservations?date=${dateStr}`)
      ]);
      setTables(tablesRes.data);
      // Filter only confirmed reservations
      const confirmed = reservationsRes.data.filter(r => r.status === 'confirmed' || r.status === 'seated');
      setReservations(confirmed);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Fout bij laden van gegevens");
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get reservations for a specific table
  const getTableReservations = (tableId) => {
    return reservations.filter(r => 
      r.table_ids && r.table_ids.includes(tableId)
    ).sort((a, b) => a.time.localeCompare(b.time));
  };

  // Count tables with reservations
  const tablesWithReservations = tables.filter(t => 
    t.type !== 'decor' && getTableReservations(t.id).length > 0
  ).length;

  const totalTables = tables.filter(t => t.type !== 'decor').length;

  const goToPreviousDay = () => setSelectedDate(subDays(selectedDate, 1));
  const goToNextDay = () => setSelectedDate(addDays(selectedDate, 1));
  const goToToday = () => setSelectedDate(new Date());

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-brand-gold text-xl">Laden...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col p-6 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif text-brand-gold" data-testid="floorplan-title">
            Zaalplan Overzicht
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Bekijk alle reserveringen per tafel
          </p>
        </div>

        {/* Date Navigation */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={goToPreviousDay}
            className="border-white/20 text-gray-300 hover:bg-white/10"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => setShowCalendar(!showCalendar)}
              className="border-white/20 text-gray-300 hover:bg-white/10 min-w-[180px]"
              data-testid="date-selector"
            >
              <CalendarDays className="w-4 h-4 mr-2" />
              {format(selectedDate, 'EEEE d MMMM', { locale: nl })}
            </Button>
            
            {showCalendar && (
              <div className="absolute top-full right-0 mt-2 z-50 bg-[#1A1A1A] border border-white/20 rounded-lg shadow-xl">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    if (date) {
                      setSelectedDate(date);
                      setShowCalendar(false);
                    }
                  }}
                  locale={nl}
                  weekStartsOn={1}
                  className="rounded-md"
                />
              </div>
            )}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={goToNextDay}
            className="border-white/20 text-gray-300 hover:bg-white/10"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={goToToday}
            className="border-white/20 text-gray-300 hover:bg-white/10 ml-2"
          >
            Vandaag
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="flex items-center gap-6 mb-4 p-4 bg-[#1A1A1A] rounded-lg border border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#6366F1]"></div>
          <span className="text-gray-300 text-sm">Bezet: {tablesWithReservations}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#374151]"></div>
          <span className="text-gray-300 text-sm">Vrij: {totalTables - tablesWithReservations}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-[#F59E0B]"></div>
          <span className="text-gray-300 text-sm">Meerdere reserveringen</span>
        </div>
        <div className="ml-auto text-gray-400 text-sm">
          Totaal reserveringen: <span className="text-brand-gold font-semibold">{reservations.length}</span>
        </div>
      </div>

      {/* Floor Plan */}
      <div className="flex-1 overflow-auto bg-[#121212] rounded-lg border border-white/10">
        <div 
          className="relative min-h-[600px]"
          style={{ 
            width: '900px',
            height: '700px',
            backgroundImage: 'radial-gradient(circle, #333 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}
          data-testid="floor-plan-container"
        >
          {tables.map(table => (
            <OverviewTable
              key={table.id}
              table={table}
              tableReservations={getTableReservations(table.id)}
            />
          ))}

          {/* Empty state */}
          {reservations.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center p-8 bg-[#1A1A1A]/80 rounded-lg border border-white/10">
                <CalendarDays className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                <p className="text-gray-400">Geen reserveringen voor deze datum</p>
                <p className="text-gray-500 text-sm mt-1">
                  {format(selectedDate, 'd MMMM yyyy', { locale: nl })}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Reservation List Summary */}
      {reservations.length > 0 && (
        <div className="mt-4 p-4 bg-[#1A1A1A] rounded-lg border border-white/10">
          <h3 className="text-brand-gold font-semibold mb-3">Alle reserveringen ({reservations.length})</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 max-h-[200px] overflow-y-auto">
            {reservations
              .sort((a, b) => a.time.localeCompare(b.time))
              .map(res => {
                const tableLabels = tables
                  .filter(t => res.table_ids?.includes(t.id))
                  .map(t => t.type === 'bar' ? `B${t.number}` : `T${t.number}`)
                  .join(' + ');
                
                return (
                  <div 
                    key={res.id} 
                    className="p-3 bg-[#222222] rounded-lg border border-white/10"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-brand-gold font-semibold">{res.time}</span>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {res.party_size}
                      </span>
                    </div>
                    <p className="text-white text-sm truncate">{res.guest_name || 'Onbekend'}</p>
                    {tableLabels ? (
                      <p className="text-xs text-green-400 mt-1">✓ {tableLabels}</p>
                    ) : (
                      <p className="text-xs text-orange-400 mt-1">⚠ Geen tafel</p>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FloorPlanOverview;
